import React from 'react'

import { NavigationAppMenuItem } from 'features/navigation/types'

import {
    Dropdown,
    DropdownButton,
    DropdownContent,
    DropdownEndIndicatorIcon,
    DropdownHeadSearch,
    DropdownItem,
    DropdownSub,
    DropdownSubContent,
    DropdownSubTrigger,
} from 'ui/components/Dropdown'
import { Tooltip } from 'ui/components/Tooltip'
import { stopPropagation } from 'ui/helpers/utilities'

import { useNavigationEditorItemVisibilityControlState } from './hooks/useNavigationEditorItemVisibilityControlState'

type NavigationEditorItemVisibilityControlProps = {
    item: NavigationAppMenuItem
    disabled?: boolean
}

export const NavigationEditorItemVisibilityControl: React.FC<
    NavigationEditorItemVisibilityControlProps
> = ({ item, disabled }) => {
    const {
        status,
        label,
        onSetEveryone,
        onSetHidden,
        isSavingSlow,
        hasRoleRestrictionSupport,
        startIcon,
        roleOptions,
        onToggleRole,
    } = useNavigationEditorItemVisibilityControlState(item)

    return (
        <Dropdown modal={false}>
            <DropdownButton
                size="2xs"
                variant="ghost"
                startIcon={startIcon}
                endIcon={{ name: 'ChevronDown' }}
                disabled={disabled}
                isLoading={isSavingSlow}
            >
                {label}
            </DropdownButton>
            <DropdownContent align="end" side="bottom">
                <DropdownItem
                    label="Everyone"
                    startIcon={{ name: 'Globe' }}
                    multiSelect
                    checked={status === 'everyone'}
                    closeOnSelect
                    onCheckedChange={onSetEveryone}
                />
                {hasRoleRestrictionSupport && (
                    <DropdownSub>
                        <DropdownSubTrigger
                            label="Restricted to roles"
                            startIcon={
                                {
                                    name: 'Lock',
                                    color: status === 'restricted' ? 'iconTheme' : undefined,
                                } as any
                            }
                        />
                        <DropdownSubContent head={<DropdownHeadSearch />}>
                            {roleOptions.map((role) => (
                                <DropdownItem
                                    key={role.value}
                                    label={role.label}
                                    multiSelect={true}
                                    checked={role.isChecked}
                                    onSelect={stopPropagation}
                                    onCheckedChange={
                                        role.canSeeAllPages
                                            ? undefined
                                            : (checked) => onToggleRole(role.value, checked)
                                    }
                                    leftSlotContent={() => {
                                        if (role.canSeeAllPages) {
                                            return (
                                                <Tooltip
                                                    content="This role can see all pages"
                                                    zIndex={9999}
                                                >
                                                    <DropdownEndIndicatorIcon
                                                        name="Lock"
                                                        color="icon"
                                                    />
                                                </Tooltip>
                                            )
                                        }

                                        return null
                                    }}
                                />
                            ))}
                        </DropdownSubContent>
                    </DropdownSub>
                )}
                <DropdownItem
                    label="Hidden"
                    multiSelect
                    startIcon={{ name: 'EyeOff' }}
                    checked={status === 'hidden'}
                    onCheckedChange={onSetHidden}
                    closeOnSelect
                />
            </DropdownContent>
        </Dropdown>
    )
}
